import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const GpQualityInspectionChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [checkAndTickPlan, setCheckAndTickPlan] = useState<Checklist[]>([]);
  const [externalChassisFrame, setExternalChassisFrame] = useState<Checklist[]>(
    []
  );
  const [externalFront, setExternalFront] = useState<Checklist[]>([]);
  const [externalRoadSide, setExternalRoadSide] = useState<Checklist[]>([]);
  const [externalRear, setExternalRear] = useState<Checklist[]>([]);
  const [externalDoorSide, setExternalDoorSide] = useState<Checklist[]>([]);
  const [internal, setInternal] = useState<Checklist[]>([]);
  const [entrance, setEntrance] = useState<Checklist[]>([]);
  const [ensuite, setEnsuite] = useState<Checklist[]>([]);
  const [kitchen, setKitchen] = useState<Checklist[]>([]);
  const [loungeDinnette, setLoungeDinnette] = useState<Checklist[]>([]);
  const [bedroom, setBedroom] = useState<Checklist[]>([]);
  const [ceiling, setCeiling] = useState<Checklist[]>([]);
  const [underneathOfVan, setUnderneathOfVan] = useState<Checklist[]>([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "gp-quality-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setCheckAndTickPlan([]);
        setExternalChassisFrame([]);
        setExternalFront([]);
        setExternalRoadSide([]);
        setExternalRear([]);
        setExternalDoorSide([]);
        setInternal([]);
        setEntrance([]);
        setEnsuite([]);
        setKitchen([]);
        setLoungeDinnette([]);
        setBedroom([]);
        setCeiling([]);
        setUnderneathOfVan([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_gp_12_quality_inspection/${chassisNo}`
        );
        const data = response.data;

        const checkAndTickPlanData = (data.checkAndTickPlan || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const externalChassisFrameData = (data.externalChassisFrame || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const externalFrontData = (data.externalFront || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const externalRoadSideData = (data.externalRoadSide || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const externalRearData = (data.externalRear || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const externalDoorSideData = (data.externalDoorSide || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const internalData = (data.internal || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const entranceData = (data.entrance || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const ensuiteData = (data.ensuite || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const kitchenData = (data.kitchen || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const loungeDinnetteData = (data.loungeDinnette || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const bedroomData = (data.bedroom || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const ceilingData = (data.ceiling || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const underneathOfVanData = (data.underneathOfVan || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        setCheckAndTickPlan(checkAndTickPlanData);
        setExternalChassisFrame(externalChassisFrameData);
        setExternalFront(externalFrontData);
        setExternalRoadSide(externalRoadSideData);
        setExternalRear(externalRearData);
        setExternalDoorSide(externalDoorSideData);
        setInternal(internalData);
        setEntrance(entranceData);
        setEnsuite(ensuiteData);
        setKitchen(kitchenData);
        setLoungeDinnette(loungeDinnetteData);
        setBedroom(bedroomData);
        setCeiling(ceilingData);
        setUnderneathOfVan(underneathOfVanData);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "Default Model");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [
          ...checkAndTickPlanData,
          ...externalChassisFrameData,
          ...externalFrontData,
          ...externalRoadSideData,
          ...externalRearData,
          ...externalDoorSideData,
          ...internalData,
          ...entranceData,
          ...ensuiteData,
          ...kitchenData,
          ...loungeDinnetteData,
          ...bedroomData,
          ...ceilingData,
          ...underneathOfVanData,
        ];
        const isComplete =
          allItems.length > 0 &&
          allItems.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        setIsRadioChanged(false);
      } catch (error: any) {
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const allChecklists = [
        ...checkAndTickPlan,
        ...externalChassisFrame,
        ...externalFront,
        ...externalRoadSide,
        ...externalRear,
        ...externalDoorSide,
        ...internal,
        ...entrance,
        ...ensuite,
        ...kitchen,
        ...loungeDinnette,
        ...bedroom,
        ...ceiling,
        ...underneathOfVan,
      ];

      if (isRadioChanged) {
        const isComplete =
          allChecklists.length > 0 &&
          allChecklists.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      }

      const payload = {
        chassisNo,
        model: model,
        checkAndTickPlan: checkAndTickPlan.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalChassisFrame: externalChassisFrame.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalFront: externalFront.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalRoadSide: externalRoadSide.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalRear: externalRear.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalDoorSide: externalDoorSide.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internal: internal.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        entrance: entrance.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        ensuite: ensuite.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        kitchen: kitchen.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        loungeDinnette: loungeDinnette.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        bedroom: bedroom.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        ceiling: ceiling.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        underneathOfVan: underneathOfVan.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_gp_12_quality_inspection",
          payload
        ),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [
          ...checkAndTickPlan,
          ...externalChassisFrame,
          ...externalFront,
          ...externalRoadSide,
          ...externalRear,
          ...externalDoorSide,
          ...internal,
          ...entrance,
          ...ensuite,
          ...kitchen,
          ...loungeDinnette,
          ...bedroom,
          ...ceiling,
          ...underneathOfVan,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const notesStr = item.notes || "";
            const getProblemDescription = (note: string) => {
              const trimmedNote = note.trim();
              return trimmedNote || "No note provided";
            };

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: getProblemDescription(note),
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: getProblemDescription(notesStr),
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "gp-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "gp-quality-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;
    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    if (type === "check_and_tick_plan") {
      updateChecklist(checkAndTickPlan, setCheckAndTickPlan);
    }
    if (type === "external_chassis_frame") {
      updateChecklist(externalChassisFrame, setExternalChassisFrame);
    }
    if (type === "external_front") {
      updateChecklist(externalFront, setExternalFront);
    }
    if (type === "external_road_side") {
      updateChecklist(externalRoadSide, setExternalRoadSide);
    }
    if (type === "external_rear") {
      updateChecklist(externalRear, setExternalRear);
    }
    if (type === "external_door_side") {
      updateChecklist(externalDoorSide, setExternalDoorSide);
    }
    if (type === "internal") {
      updateChecklist(internal, setInternal);
    }
    if (type === "entrance") {
      updateChecklist(entrance, setEntrance);
    }
    if (type === "ensuite") {
      updateChecklist(ensuite, setEnsuite);
    }
    if (type === "kitchen") {
      updateChecklist(kitchen, setKitchen);
    }
    if (type === "lounge_dinnette") {
      updateChecklist(loungeDinnette, setLoungeDinnette);
    }
    if (type === "bedroom") {
      updateChecklist(bedroom, setBedroom);
    }
    if (type === "ceiling") {
      updateChecklist(ceiling, setCeiling);
    }
    if (type === "underneath_of_van") {
      updateChecklist(underneathOfVan, setUnderneathOfVan);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "check_and_tick_plan") {
      updateChecklist(checkAndTickPlan, setCheckAndTickPlan);
    }
    if (type === "external_chassis_frame") {
      updateChecklist(externalChassisFrame, setExternalChassisFrame);
    }
    if (type === "external_front") {
      updateChecklist(externalFront, setExternalFront);
    }
    if (type === "external_road_side") {
      updateChecklist(externalRoadSide, setExternalRoadSide);
    }
    if (type === "external_rear") {
      updateChecklist(externalRear, setExternalRear);
    }
    if (type === "external_door_side") {
      updateChecklist(externalDoorSide, setExternalDoorSide);
    }
    if (type === "internal") {
      updateChecklist(internal, setInternal);
    }
    if (type === "entrance") {
      updateChecklist(entrance, setEntrance);
    }
    if (type === "ensuite") {
      updateChecklist(ensuite, setEnsuite);
    }
    if (type === "kitchen") {
      updateChecklist(kitchen, setKitchen);
    }
    if (type === "lounge_dinnette") {
      updateChecklist(loungeDinnette, setLoungeDinnette);
    }
    if (type === "bedroom") {
      updateChecklist(bedroom, setBedroom);
    }
    if (type === "ceiling") {
      updateChecklist(ceiling, setCeiling);
    }
    if (type === "underneath_of_van") {
      updateChecklist(underneathOfVan, setUnderneathOfVan);
    }
  };

  const allChecklists = [
    ...checkAndTickPlan,
    ...externalChassisFrame,
    ...externalFront,
    ...externalRoadSide,
    ...externalRear,
    ...externalDoorSide,
    ...internal,
    ...entrance,
    ...ensuite,
    ...kitchen,
    ...loungeDinnette,
    ...bedroom,
    ...ceiling,
    ...underneathOfVan,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  console.log("All Checklists:", allChecklists);
  console.log("allNotChecked:", allNotChecked);

  const checklistsWithSerialNumbers = [
    {
      data: checkAndTickPlan,
      type: "check_and_tick_plan",
      subHeaderText: "Check & tick PLAN for any missing items",
    },
    {
      data: externalChassisFrame,
      type: "external_chassis_frame",
      subHeaderText: "External - Chassis A Frame",
    },
    {
      data: externalFront,
      type: "external_front",
      subHeaderText: "External - Front Of Van",
    },
    {
      data: externalRoadSide,
      type: "external_road_side",
      subHeaderText: "External - Road Side Of Van",
    },
    {
      data: externalRear,
      type: "external_rear",
      subHeaderText: "External - Rear Of Van",
    },
    {
      data: externalDoorSide,
      type: "external_door_side",
      subHeaderText: "External - Door Side Of Van",
    },
    {
      data: internal,
      type: "internal",
      subHeaderText: "Internal",
    },
    {
      data: entrance,
      type: "entrance",
      subHeaderText: "Entrance",
    },
    {
      data: ensuite,
      type: "ensuite",
      subHeaderText: "Ensuite",
    },
    {
      data: kitchen,
      type: "kitchen",
      subHeaderText: "Kitchen",
    },
    {
      data: loungeDinnette,
      type: "lounge_dinnette",
      subHeaderText: "Lounge - Dinette",
    },
    {
      data: bedroom,
      type: "bedroom",
      subHeaderText: "Bedroom",
    },
    {
      data: ceiling,
      type: "ceiling",
      subHeaderText: "Ceiling",
    },
    {
      data: underneathOfVan,
      type: "underneath_of_van",
      subHeaderText: "Others",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"gp-quality-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default GpQualityInspectionChecklist;
